import React, { useState, useEffect } from "react";
import axios from "axios";
import { Star, Quote, PenSquare, ExternalLink } from "lucide-react";
import { reviews as mockReviews, businessInfo } from "../mock";
import { Button } from "./ui/button";
import ReviewForm from "./ReviewForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatRelative = (iso) => {
  try {
    const then = new Date(iso);
    const diff = Date.now() - then.getTime();
    const day = 86400000;
    if (diff < day) return "today";
    if (diff < 2 * day) return "yesterday";
    if (diff < 7 * day) return `${Math.floor(diff / day)} days ago`;
    if (diff < 30 * day) return `${Math.floor(diff / (7 * day))} weeks ago`;
    if (diff < 365 * day) return `${Math.floor(diff / (30 * day))} months ago`;
    return `${Math.floor(diff / (365 * day))} years ago`;
  } catch {
    return "recently";
  }
};

const Reviews = () => {
  const [formOpen, setFormOpen] = useState(false);
  const [liveReviews, setLiveReviews] = useState([]);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/reviews?limit=24`)
      .then((res) => {
        if (cancelled) return;
        const normalized = (res.data || []).map((r) => ({
          id: r.id,
          name: r.name,
          location: r.location,
          rating: r.rating,
          text: r.text,
          date: formatRelative(r.created_at),
        }));
        setLiveReviews(normalized);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Live (approved) reviews first, then seed mock reviews to fill the section
  const allReviews = [...liveReviews, ...mockReviews];

  return (
    <section id="reviews" className="py-20 lg:py-28 fire-bg-subtle">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div className="max-w-2xl">
            <span className="text-yellow-400 font-bold uppercase tracking-[0.3em] text-xs">
              Customer Reviews
            </span>
            <h2 className="mt-3 text-white font-extrabold text-3xl lg:text-5xl tracking-tight heading-underline">
              5 Stars on Yelp
            </h2>
            <p className="mt-5 text-zinc-400 text-base lg:text-lg leading-relaxed">
              Real experiences from drivers across the Tri-State. Had a great
              experience with Shane? Share it below.
            </p>
          </div>
          <div className="flex items-center gap-4 brand-card rounded-xl px-5 py-4 border-yellow-500/30">
            <div className="text-4xl font-extrabold text-gold-shine">5.0</div>
            <div>
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className="w-4 h-4 fill-yellow-400 text-yellow-400"
                  />
                ))}
              </div>
              <div className="text-xs text-zinc-400 mt-1">
                Based on customer reviews
              </div>
            </div>
          </div>
        </div>

        {/* Action bar */}
        <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:items-center">
          <Button
            onClick={() => setFormOpen(true)}
            className="bg-brand-red text-white font-semibold h-12 px-6"
          >
            <PenSquare className="w-4 h-4 mr-2" />
            Leave a Review
          </Button>
          <Button
            asChild
            variant="outline"
            className="h-12 px-6 bg-white/5 border-yellow-500/40 text-yellow-300 hover:bg-yellow-500 hover:text-black hover:border-yellow-500 font-semibold"
          >
            <a
              href={businessInfo.yelpReviewUrl}
              target="_blank"
              rel="noreferrer"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Or Review on Yelp
            </a>
          </Button>
          <p className="text-zinc-500 text-xs sm:ml-2">
            New reviews appear after Shane approves them.
          </p>
        </div>

        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {allReviews.map((r) => (
            <div
              key={r.id}
              className="brand-card rounded-xl p-6 flex flex-col hover:border-red-600 transition-colors duration-300"
            >
              <Quote className="w-7 h-7 text-red-600/40" />
              <div className="flex gap-0.5 mt-2">
                {[...Array(r.rating)].map((_, i) => (
                  <Star
                    key={i}
                    className="w-4 h-4 fill-yellow-400 text-yellow-400"
                  />
                ))}
              </div>
              <p className="mt-3 text-zinc-300 text-[15px] leading-relaxed flex-1">
                {`\u201c${r.text}\u201d`}
              </p>
              <div className="mt-5 pt-4 border-t border-zinc-800">
                <div className="font-bold text-white">{r.name}</div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  {r.location} {"\u00b7"} {r.date}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <ReviewForm open={formOpen} onOpenChange={setFormOpen} />
    </section>
  );
};

export default Reviews;
